import { sortByAll } from 'lib/array/arrayUtils'
import type { SurfaceRecentCommittedStateRequest } from 'tripPlanner/assistant/api/surfaceSnapshotContract'

// Shared by every surface builder (offer page, trip planner, ...) that assembles
// recentCommittedState -- extracted rather than left offer-page-local so a second surface
// needing the same sort/dedupe rules imports this instead of re-deriving it a second time.
// Sorts newest-expiring-first, then by kind/key for stable ordering among ties. Offer page's own
// handful of hardcoded keys never collide today, but trip_planner's stable per-stop/per-leg keys
// scale with trip size, so a genuine same-key collision (the accumulator overwriting a key,
// re-pushing it) is expected, not exceptional -- the dedupe step below is a no-op today for offer
// page and a real guard for trip_planner. Keeps the first (highest-sorted) entry for a colliding
// key, so a longer-lived duplicate wins over a shorter-lived one. Generic (rather than fixed to
// the base wire type) so a caller passing a narrower entry type -- e.g. trip_planner's
// TripPlannerCommittedStateEntry -- gets that same narrower type back, with no cast needed at the
// call site.
//
// Deliberately not lib/array/arrayUtils's generic uniqueBy here: `key` is optional on the base
// wire type (though every current producer always sets it), and uniqueBy(sorted, state =>
// state.key ?? '') would collapse any two keyless entries into one via the shared '' fallback,
// silently dropping a real entry. Filtering by hand instead means a missing key never counts as a
// collision with another missing key -- only two entries that share the same real key do.
export function sortCommittedState<T extends SurfaceRecentCommittedStateRequest>(
  committed: Array<T>,
): Array<T> {
  const sorted = sortByAll(committed, [
    { selector: state => state.expiresAt ?? '', direction: 'desc' },
    { selector: state => state.kind ?? '', direction: 'asc' },
    { selector: state => state.key ?? '', direction: 'asc' },
  ])
  const seenKeys = new Set<string>()
  return sorted.filter(state => {
    if (!state.key) return true
    if (seenKeys.has(state.key)) return false
    seenKeys.add(state.key)
    return true
  })
}
